import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Divider,
} from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import PapiGrillingCui from "./Papi_Grilling_Cui.jpg";
import PapiConFamilia from "./Papi_Con_Familia.jpg";
import PapiEnParty from "./Papi_En_Party.jpg";
import Papi from "./Papi.jpg";
import FaustoIllustrated from "./fausto.jpg";

// Cuenca palette extracted from the illustrated image
const COLORS = {
  terracotta: "#C95A2C",
  terracottaDark: "#8B3A1A",
  terracottaLight: "#E07A4A",
  skyBlue: "#4A8BC4",
  skyBlueDark: "#2C5F8A",
  skyBlueLight: "#7AB5DC",
  teal: "#2A9D8F",
  tealLight: "#4ABFB2",
  colonial: "#D4A847",
  colonialLight: "#EBC96A",
  sand: "#F2E8D8",
  sandDark: "#D4C4A8",
  cobblestone: "#6B6878",
  navy: "#1E2D4A",
  cream: "#FAF6EF",
  warmWhite: "#F5F0E8",
};

const theme = createTheme({
  palette: {
    primary: { main: COLORS.terracotta },
    secondary: { main: COLORS.skyBlue },
    background: { default: COLORS.sand },
    text: { primary: COLORS.navy, secondary: COLORS.cobblestone },
  },
  typography: {
    fontFamily: '"Playfair Display", "Lora", Georgia, serif',
  },
});

// Floating particles inspired by the Cuenca scene
const particles = [
  { symbol: "⚽", size: "1.8rem" },
  { symbol: "🥊", size: "1.6rem" },
  { symbol: "⭐", size: "1.4rem" },
  { symbol: "🌟", size: "1.6rem" },
  { symbol: "❤️", size: "1.5rem" },
  { symbol: "🕊️", size: "1.6rem" },
  { symbol: "⚽", size: "2rem" },
  { symbol: "💙", size: "1.4rem" },
  { symbol: "🇪🇨", size: "1.6rem" },
  { symbol: "🌹", size: "1.5rem" },
  { symbol: "⭐", size: "1.8rem" },
  { symbol: "🥊", size: "1.7rem" },
  { symbol: "🕊️", size: "1.4rem" },
  { symbol: "❤️", size: "1.6rem" },
];

const FloatingParticle = ({
  symbol,
  size,
  delay,
  startX,
}: {
  symbol: string;
  size: string;
  delay: number;
  startX: number;
}) => (
  <motion.div
    initial={{ y: "-10vh", x: startX, opacity: 0.8, rotate: 0 }}
    animate={{
      y: "110vh",
      x: startX + (Math.random() - 0.5) * 200,
      opacity: [0, 0.9, 0.7, 0],
      rotate: Math.random() > 0.5 ? 360 : -360,
    }}
    transition={{
      duration: 14 + Math.random() * 10,
      repeat: Infinity,
      ease: "linear",
      delay,
    }}
    style={{
      position: "fixed",
      fontSize: size,
      zIndex: 1,
      pointerEvents: "none",
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
    }}
  >
    {symbol}
  </motion.div>
);

// Decorative terracotta tile divider
const TileDivider = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 4 }}>
    <Box sx={{ flex: 1, height: "2px", background: `linear-gradient(to right, transparent, ${COLORS.terracotta})` }} />
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {["🔶", "⬛", "🔶"].map((s, i) => (
        <Typography key={i} sx={{ fontSize: "0.7rem", opacity: 0.7 }}>{s}</Typography>
      ))}
    </Box>
    <Box sx={{ flex: 1, height: "2px", background: `linear-gradient(to left, transparent, ${COLORS.terracotta})` }} />
  </Box>
);

// Rocky badge component
const RockyBadge = () => (
  <motion.div
    initial={{ scale: 0, rotate: -12 }}
    animate={{ scale: 1, rotate: -6 }}
    transition={{ type: "spring", stiffness: 200, delay: 1.2 }}
    style={{ display: "inline-block" }}
  >
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        px: 3,
        py: 1.5,
        borderRadius: "50%",
        width: { xs: 100, sm: 120 },
        height: { xs: 100, sm: 120 },
        justifyContent: "center",
        background: `radial-gradient(circle, ${COLORS.terracotta} 0%, ${COLORS.terracottaDark} 100%)`,
        border: `4px solid ${COLORS.colonialLight}`,
        boxShadow: `0 8px 24px rgba(201,90,44,0.5), inset 0 2px 4px rgba(255,255,255,0.2)`,
        position: "relative",
      }}
    >
      <Typography sx={{ fontSize: "1.5rem", lineHeight: 1 }}>🥊</Typography>
      <Typography
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 800,
          fontSize: { xs: "1rem", sm: "1.1rem" },
          color: "#fff",
          letterSpacing: "0.05em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        Rocky
      </Typography>
    </Box>
  </motion.div>
);

// Star memorial decoration
const StarMemorial = () => (
  <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 1, sm: 2 }, flexWrap: "wrap", mt: 2 }}>
    {["✨", "⭐", "🌟", "⭐", "✨"].map((s, i) => (
      <motion.span
        key={i}
        animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.1, 0.9] }}
        transition={{ duration: 2.5, delay: i * 0.4, repeat: Infinity }}
        style={{ fontSize: i === 2 ? "2rem" : "1.3rem" }}
      >
        {s}
      </motion.span>
    ))}
  </Box>
);

const App: React.FC = () => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 500], [0, -80]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 600;

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 900,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    swipe: true,
    touchMove: true,
    fade: true,
    cssEase: "cubic-bezier(0.7, 0, 0.3, 1)",
  };

  const photos = [Papi, PapiGrillingCui, PapiConFamilia, PapiEnParty];
  const photoFocusPoints = ["center 10%", "center 30%", "center 10%", "center 50%"];

  const message = `Rocky!

I know you've been watching everything that's been going on in this world. You're watching everyone and we can feel it. There has been a lot going on lately, but we all miss you dearly. You were and still are the definition of what a family man is. You never backed down to any confrontation especially from what Abby tells me. I've also seen it firsthand when you had to step in during a potential situation at a party. I always respected you and still do. I truly miss having you around and everyone misses you deeply. You are a true definition of what a father is.

We all miss you hosting the family events. You were always a family man, and I still remember when I first met you how scared I was. By then I deeply loved Abby and still do. I wanted to impress you and not look like another person just trying to get with your daughter. I already had the mentality of building a family with her and expanding your family by being a part of it. I love every bit of your welcoming. I remember your conversations and your deep heart to heart you had with me informing me to take care of Abby and Danny. My own father never really approached me and provided me any insight, you did.

Every time I see anything resembling Rocky I think of you. I remember after Jordan's graduation you were sitting in the back seat with everyone and was expressing how fit you are since you started going to the gym. I laughed not to laugh at you, but I heard it in your voice the sarcasm and expressing how you are the definition of Rocky. I loved hearing Abby laugh too and then smacking me on the shoulder for laughing after you expressing your fitness.

I know Abby talks to you either out loud or mentally. She misses you deeply, but I know she feels you around protecting her. She is not someone I want to disappoint, and I want to apologize to you for the moments that I did. I love her and I want to be with her forever. She is my one and only. You molded her to be an amazing person who I deeply fell in love with. I told her I loved her after a month and a half. I immediately felt she was the one. I want to thank you for blessing me with such an amazing and gorgeous woman.

Right now, as I write this it feels as if you're next to me looking at me with your arms folded. I know you're giving me boche for many things and I understand. That feeling alone of you watching me right now with your arms folded is giving me chills down my spine because I've seen you serious and it's like being shot and stabbed at the same time. That fear I had when I first met you is not comparable to the feeling of now with you looking at me shaking your head and expressing to me your disappointment. But I will succeed. I will do anything.

I love you Fausto, Rocky, Papi. We all miss you so much and hope you're traveling everywhere. I know you're consistently watching over Abby too. You go to Ecuador and then make your rounds to all of us — that's just who you are. We feel you in every room, every gathering, every moment that matters.

Con todo mi amor y respeto,
Alex`;

  const rockyEnding = `Champions don't disappear — they become the voice in our head that makes us get back up. Happy Father's Day, Champ. 🥊🏆❤️`;


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Floating particles layer */}
      <Box sx={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 20, overflow: "hidden" }}>
        {particles.map((p, i) => (
          <FloatingParticle
            key={i}
            symbol={p.symbol}
            size={p.size}
            delay={i * 1.8}
            startX={Math.floor(Math.random() * (windowWidth - 60))}
          />
        ))}
      </Box>

      {/* Page background */}
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(180deg,
            #1A2C4E 0%,
            #2C4A7A 8%,
            #4A7DB5 18%,
            #7AB5DC 28%,
            ${COLORS.sandDark} 40%,
            ${COLORS.sand} 60%,
            ${COLORS.warmWhite} 100%
          )`,
          position: "relative",
          overflowX: "hidden",
        }}
      >
        {/* ── HERO SECTION ── */}
        <Box
          sx={{
            position: "relative",
            minHeight: { xs: "95vh", md: "100vh" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-end",
            overflow: "hidden",
            pb: { xs: 4, md: 6 },
          }}
        >
          {/* Hero background – illustrated Fausto image */}
          <motion.div style={{ y: heroParallax, position: "absolute", inset: 0 }}>
            <Box
              component="img"
              src={FaustoIllustrated}
              alt="Fausto in Cuenca"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                display: "block",
              }}
            />
            {/* Gradient overlay to fade into the page */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(
                  to bottom,
                  rgba(26,44,78,0.25) 0%,
                  rgba(26,44,78,0.1) 40%,
                  rgba(242,232,216,0.6) 75%,
                  ${COLORS.sand} 100%
                )`,
              }}
            />
          </motion.div>

          {/* Hero text overlay */}
          <Box sx={{ position: "relative", zIndex: 2, textAlign: "center", px: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              {/* "Forever in our hearts" label */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 3,
                  py: 0.8,
                  borderRadius: 10,
                  background: "rgba(201,90,44,0.85)",
                  backdropFilter: "blur(8px)",
                  border: `1px solid rgba(235,201,106,0.5)`,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    fontWeight: 600,
                    color: COLORS.colonialLight,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    fontFamily: '"Playfair Display", serif',
                  }}
                >
                  Forever In Our Hearts
                </Typography>
              </Box>

              {/* Main name title */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "3rem", sm: "4.5rem", md: "6rem" },
                  fontWeight: 800,
                  fontFamily: '"Playfair Display", serif',
                  color: "#fff",
                  textShadow: `3px 3px 0 ${COLORS.terracottaDark}, 0 6px 30px rgba(0,0,0,0.5)`,
                  lineHeight: 1,
                  mb: 1,
                }}
              >
                Fausto
              </Typography>

              {/* Rocky nickname */}
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, mb: 1 }}>
                <Box sx={{ width: 40, height: 2, background: COLORS.colonialLight, borderRadius: 1 }} />
                <Typography
                  sx={{
                    fontSize: { xs: "1.1rem", sm: "1.5rem" },
                    fontWeight: 600,
                    color: COLORS.colonialLight,
                    fontFamily: '"Playfair Display", serif',
                    fontStyle: "italic",
                    letterSpacing: "0.1em",
                  }}
                >
                  "Rocky"
                </Typography>
                <Box sx={{ width: 40, height: 2, background: COLORS.colonialLight, borderRadius: 1 }} />
              </Box>

              {/* Father's Day title */}
              <Typography
                sx={{
                  fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.2rem" },
                  fontWeight: 700,
                  fontFamily: '"Playfair Display", serif',
                  background: `linear-gradient(90deg, ${COLORS.colonialLight}, #fff, ${COLORS.tealLight})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "none",
                  mb: 3,
                }}
              >
                Feliz Día del Padre 🇪🇨
              </Typography>

              <StarMemorial />
            </motion.div>
          </Box>
        </Box>

        {/* ── MAIN CONTENT ── */}
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 5 }}>
          <Box sx={{ pb: { xs: 4, md: 8 } }}>

            {/* ── IN MEMORIAM BANNER ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  mb: 4,
                  mt: { xs: -2, md: -4 },
                  position: "relative",
                }}
              >
                <RockyBadge />
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      color: COLORS.cobblestone,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    He still lives on with us — always
                  </Typography>
                </Box>
              </Box>
            </motion.div>

            <TileDivider />

            {/* ── PHOTO GALLERY ── */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  mb: 3,
                  background: "rgba(255,255,255,0.65)",
                  backdropFilter: "blur(16px)",
                  border: `2px solid rgba(201,90,44,0.2)`,
                  boxShadow: `0 24px 60px rgba(30,45,74,0.15), 0 4px 16px rgba(201,90,44,0.1)`,
                }}
              >
                {/* Gallery header */}
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${COLORS.terracottaDark} 0%, ${COLORS.terracotta} 50%, ${COLORS.colonial} 100%)`,
                    py: 2,
                    px: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      color: "#fff",
                      letterSpacing: "0.08em",
                    }}
                  >
                    📸 &nbsp; Momentos con Fausto &nbsp; 📸
                  </Typography>
                </Box>

                <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 5, sm: 6 } }}>
                  <Slider {...sliderSettings}>
                    {photos.map((photo, i) => (
                      <Box key={i}>
                        <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.3 }}>
                          <Box
                            component="img"
                            src={photo}
                            alt={`Fausto memory ${i + 1}`}
                            sx={{
                              width: "100%",
                              height: isMobile ? "260px" : "480px",
                              objectFit: "cover",
                              objectPosition: photoFocusPoints[i],
                              borderRadius: 3,
                              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                              display: "block",
                            }}
                          />
                        </motion.div>
                      </Box>
                    ))}
                  </Slider>
                </Box>
              </Paper>
            </motion.div>

            {/* ── LEGACY CARDS ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                  mb: 4,
                }}
              >
                {[
                  { icon: "👨‍👩‍👧‍👦", title: "Family Man", text: "His heart was always with his family — the foundation of everything." },
                  { icon: "🥊", title: "Rocky", text: "Tough in spirit, tender in love. He earned that name with pride." },
                  { icon: "🇪🇨", title: "Cuencano", text: "Born under Cuenca's cathedral skies, his soul was forever rooted there." },
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -4, scale: 1.02 }}
                    transition={{ duration: 0.25 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        textAlign: "center",
                        borderRadius: 3,
                        background: i === 0
                          ? `linear-gradient(135deg, rgba(201,90,44,0.12), rgba(201,90,44,0.04))`
                          : i === 1
                          ? `linear-gradient(135deg, rgba(74,139,196,0.12), rgba(74,139,196,0.04))`
                          : `linear-gradient(135deg, rgba(42,157,143,0.12), rgba(42,157,143,0.04))`,
                        border: `1.5px solid ${i === 0 ? "rgba(201,90,44,0.25)" : i === 1 ? "rgba(74,139,196,0.25)" : "rgba(42,157,143,0.25)"}`,
                        backdropFilter: "blur(8px)",
                      }}
                    >
                      <Typography sx={{ fontSize: "2rem", mb: 1 }}>{card.icon}</Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Playfair Display", serif',
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: i === 0 ? COLORS.terracotta : i === 1 ? COLORS.skyBlueDark : COLORS.teal,
                          mb: 0.5,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: COLORS.cobblestone,
                          lineHeight: 1.5,
                          fontStyle: "italic",
                        }}
                      >
                        {card.text}
                      </Typography>
                    </Paper>
                  </motion.div>
                ))}
              </Box>
            </motion.div>

            {/* ── THE LETTER ── */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 4, md: 5 },
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.72)",
                  backdropFilter: "blur(18px)",
                  border: `1px solid rgba(201,90,44,0.15)`,
                  boxShadow: `0 30px 70px rgba(30,45,74,0.12)`,
                  position: "relative",
                  overflow: "hidden",
                  mb: 4,
                }}
              >
                {/* Decorative corner accent */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 80,
                    height: 80,
                    background: `linear-gradient(135deg, ${COLORS.terracotta}22, transparent)`,
                    borderBottomRightRadius: "100%",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 80,
                    height: 80,
                    background: `linear-gradient(315deg, ${COLORS.skyBlue}22, transparent)`,
                    borderTopLeftRadius: "100%",
                  }}
                />

                {/* Letter header */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.4rem", sm: "1.8rem" },
                      fontWeight: 700,
                      fontFamily: '"Playfair Display", serif',
                      color: COLORS.terracotta,
                      mb: 0.5,
                    }}
                  >
                    Una Carta Para Ti, Fausto
                  </Typography>
                  <Divider
                    sx={{
                      borderColor: COLORS.colonial,
                      borderWidth: "1.5px",
                      width: "60%",
                      mx: "auto",
                      opacity: 0.5,
                    }}
                  />
                </Box>

                {/* Main letter */}
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: { xs: 1.85, md: 2.1 },
                    fontSize: { xs: "0.97rem", sm: "1.05rem", md: "1.15rem" },
                    color: COLORS.navy,
                    fontFamily: '"Playfair Display", serif',
                    textAlign: "justify",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {message}
                </Typography>

                {/* Rocky motivational ending */}
                <Box
                  sx={{
                    mt: 4,
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    background: `linear-gradient(135deg, rgba(201,90,44,0.1) 0%, rgba(74,139,196,0.08) 100%)`,
                    border: `2px solid rgba(201,90,44,0.25)`,
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Typography sx={{ fontSize: "1.4rem" }}>🥊</Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 700,
                        fontSize: { xs: "1rem", sm: "1.15rem" },
                        color: COLORS.terracotta,
                        letterSpacing: "0.05em",
                      }}
                    >
                      For Rocky — The Champion
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      whiteSpace: "pre-line",
                      lineHeight: { xs: 1.85, md: 2.1 },
                      fontSize: { xs: "0.95rem", sm: "1.02rem", md: "1.1rem" },
                      color: COLORS.navy,
                      fontFamily: '"Playfair Display", serif',
                      fontStyle: "italic",
                    }}
                  >
                    {rockyEnding}
                  </Typography>
                </Box>

              </Paper>
            </motion.div>

            {/* ── MEMORIAL FOOTER ── */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  py: { xs: 3, sm: 5 },
                  px: 2,
                  borderRadius: 4,
                  background: `linear-gradient(135deg,
                    rgba(201,90,44,0.08) 0%,
                    rgba(74,139,196,0.08) 50%,
                    rgba(42,157,143,0.08) 100%)`,
                  border: `1px solid rgba(201,90,44,0.12)`,
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.4rem", sm: "2rem" },
                    fontWeight: 800,
                    fontFamily: '"Playfair Display", serif',
                    color: COLORS.terracotta,
                    mb: 1,
                  }}
                >
                  Fausto &quot;Rocky&quot; — Vive Para Siempre
                </Typography>
                <Typography
                  sx={{
                    fontStyle: "italic",
                    color: COLORS.cobblestone,
                    fontSize: { xs: "0.9rem", sm: "1.05rem" },
                    fontFamily: '"Playfair Display", serif',
                    mb: 2,
                    lineHeight: 1.7,
                  }}
                >
                  His spirit runs through every cobblestone in Cuenca, through every laugh,
                  <br />
                  every blessing, every sunrise over the cathedral domes.
                  <br />
                  He doesn't just live in memory — he lives in us.
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                  {["🕊️", "❤️", "🇪🇨", "⭐", "🕊️"].map((e, i) => (
                    <motion.span
                      key={i}
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 2.5, delay: i * 0.5, repeat: Infinity }}
                      style={{ fontSize: "1.5rem" }}
                    >
                      {e}
                    </motion.span>
                  ))}
                </Box>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "0.8rem",
                    color: COLORS.cobblestone,
                    opacity: 0.7,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Cuenca, Ecuador 🇪🇨 — Siempre en Nuestros Corazones
                </Typography>
              </Box>
            </motion.div>

          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;
